import { useCallback, useEffect, useState } from 'react';
import { api } from './api.ts';
import type { PreviewStatusPayload } from '../../shared/api.ts';

/**
 * WHICH VERSION OF A SLIDE IS ON THE CANVAS, as one shared fact.
 *
 * A live review is flippable from three places at once — the on-slide banner
 * (components/ProposalBanner.jsx), the Activity row (canvas/ActivityPanel.jsx)
 * and the chat card (components/ToolCard.jsx) — and each of them used to keep
 * its OWN `showing` state, defaulting to "candidate". Only the banner ever
 * asked the server. So flipping the banner to Before left the Activity row
 * still saying After for the same slide, and the pills disagreed about what
 * the user was plainly looking at (Jake, 2026-08-01).
 *
 * There is only one answer to this question — the file on disk, which
 * `toggle-preview` swaps — so it lives in one store, keyed by slide, seeded
 * from the server on first read and updated by every verb in
 * proposalActions.js. Surfaces subscribe; none of them owns it.
 *
 * Vocabulary is the FlipPills one: 'current' = the slide as it stands,
 * 'candidate' = the change under review. The wire says original/proposed.
 */

/** 'current' = the slide as it stands, 'candidate' = the change under review. */
export type FlipShowing = 'current' | 'candidate';

type SlideRef = string | null | undefined;

const state = new Map<string, FlipShowing>(); // `${slug}::${slideId}` → showing
const subs = new Set<() => void>();
const inflight = new Map<string, Promise<void>>();

const keyOf = (slug: SlideRef, slideId: SlideRef) => (slug && slideId ? `${slug}::${slideId}` : null);

function notify() {
  for (const fn of subs) fn();
}

/** The wire's word for the side on disk → the pills' word. */
export const showingFromWire = (wire: PreviewStatusPayload['showing']): FlipShowing =>
  wire === 'original' ? 'current' : 'candidate';

export function setFlipShowing(slug: SlideRef, slideId: SlideRef, showing: FlipShowing) {
  const key = keyOf(slug, slideId);
  if (!key) return;
  if (state.get(key) === showing) return;
  state.set(key, showing);
  notify();
}

/** A review that has ended has no side to be on: the next one starts fresh on
 *  the candidate rather than inheriting the last flip. */
export function clearFlipShowing(slug: SlideRef, slideId: SlideRef) {
  const key = keyOf(slug, slideId);
  if (!key || !state.has(key)) return;
  state.delete(key);
  notify();
}

/** Ask the server which side is live. Deduped per slide — three surfaces mount
 *  against the same review at the same moment. */
function seedFromServer(slug: SlideRef, slideId: SlideRef) {
  const key = keyOf(slug, slideId);
  if (!key || !slideId || inflight.has(key)) return;
  const p = api
    .get<Partial<PreviewStatusPayload>>(`/api/chat/${slug}/_slide/${encodeURIComponent(slideId)}/preview-status`)
    .then((res) => {
      if (res?.previewing) setFlipShowing(slug, slideId, showingFromWire(res.showing ?? null));
    })
    .catch(() => {})
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, p);
}

/**
 * `[showing, setShowing]` for one slide's live review. `seed: false` for a
 * review with no two sides to ask about — a pending CREATE has no original,
 * so preview-status has nothing to say.
 */
export function useFlipShowing(
  slug: SlideRef,
  slideId: SlideRef,
  { seed = true }: { seed?: boolean } = {},
): [FlipShowing, (next: FlipShowing) => void] {
  const key = keyOf(slug, slideId);
  const [showing, setLocal] = useState<FlipShowing>(() => (key && state.get(key)) || 'candidate');

  useEffect(() => {
    if (!key) return;
    const sync = () => setLocal(state.get(key) || 'candidate');
    sync();
    subs.add(sync);
    if (seed && !state.has(key)) seedFromServer(slug, slideId);
    return () => {
      subs.delete(sync);
    };
  }, [key, seed, slug, slideId]);

  const set = useCallback((next: FlipShowing) => setFlipShowing(slug, slideId, next), [slug, slideId]);
  return [showing, set];
}
